<template>
  <div class="rounded-lg border border-gray-200 p-3 mb-4">
    <div class="flex items-start justify-between gap-3">
      <div>
        <div class="text-sm font-medium">
          {{ useT('addon.credits') }}
        </div>
        <div class="text-xs text-gray-500">
          {{ useT('addon.credits.description') }}
        </div>
      </div>
      <div class="text-sm font-medium whitespace-nowrap">
        {{ useT('addon.credits.remaining', { left: totalLeft, allocation: totalAllocation }) }}
      </div>
    </div>

    <div
      v-if="!creditSets.length"
      class="text-xs text-gray-500 mt-3"
    >
      {{ useT('addon.credits.empty') }}
    </div>

    <!-- The order the interface spends them in (see `drainOrder`), so the top
         row is the one draining. A row opens the set in its own dialog. New credits come only from an
         add-on, so the list offers no add. -->
    <DynamicList
      v-else
      :model-value="creditSets"
      :sortable="false"
      :show-add="false"
      show-dividers
      class="mt-2"
    >
      <!-- An expired set is listed, because it is still in the document until
           the interface prunes it, but it is not part of the balance and
           editing its remainder would change nothing. -->
      <DynamicListItem
        v-for="creditSet in sortedSets"
        :key="keyOf(creditSet)"
        :disabled="isExpired(creditSet)"
        :class="isExpired(creditSet) ? 'opacity-60' : ''"
        data-cy="credit-set"
        @click="onOpen(creditSet)"
      >
        <!-- The set's type, then its state; the granted date sits on the right,
             as in the other dynamic lists. The end date is in the dialog. -->
        <span class="flex items-center gap-2">
          {{ useT(typeKeyOf(creditSet)) }}
          <Badge
            :text="statusOf(creditSet)"
            :type="isExpired(creditSet) ? 'warning' : 'neutral'"
          />
        </span>
        <template #suffix>
          <!-- `mr-8` holds the chevron's place, which a disabled row has none of,
               so the dates line up. -->
          <span
            class="tabular-nums"
            :class="isExpired(creditSet) ? 'mr-8' : ''"
          >
            {{ formatDate(creditSet.receivedAt) }}
          </span>
        </template>
      </DynamicListItem>
    </DynamicList>
  </div>
</template>

<script setup>
// Props
const props = defineProps({
  workspaceDomain: {
    type: String,
    required: true,
  },
});

// Computed
const creditSets = computed(() => useAddonsStore().ai?.credits || []);

// Expired sets last, as they are no longer spent.
const sortedSets = computed(() => [...creditSets.value].sort((first, second) =>
  Number(isExpired(first)) - Number(isExpired(second)) || drainOrder(first, second)));

// The balance the workspace can actually spend, which is what the AI widget
// shows the customer: expired sets are excluded even while they sit in the
// document.
const totalLeft = computed(() => creditSets.value
  .filter((creditSet) => !isExpired(creditSet))
  .reduce((sum, creditSet) => sum + (creditSet.left || 0), 0));

// The granted numbers of the same sets, the denominator the interface renders
// its progress bar against (`left / sum(allocation)` over unexpired sets).
const totalAllocation = computed(() => creditSets.value
  .filter((creditSet) => !isExpired(creditSet))
  .reduce((sum, creditSet) => sum + (creditSet.allocation || creditSet.left || 0), 0));

// Methods
// `receivedAt` identifies a set here for the same reason the API addresses one
// by it: console-written sets carry no `_id`, interface-written ones do, and an
// index shifts as soon as an expired set is dropped.
const keyOf = (creditSet) => new Date(creditSet.receivedAt).toISOString();

// The type comes from the add-on that granted the set. Sets granted before
// they named it have no add-on, so they get a neutral title.
const typeKeyOf = (creditSet) => {
  const addon = creditSet.addon &&
    useAddonsStore().addons.find((item) => String(item._id) === String(creditSet.addon));
  if (!addon) {
    return 'addon.credits.type.other';
  }

  return addon.data?.refillPeriod === 'monthly' ? 'addon.credits.type.monthly' : 'addon.credits.type.oneTime';
};

const statusOf = (creditSet) => {
  if (isExpired(creditSet)) {
    return useT('addon.credits.expired');
  }

  return useT('addon.credits.setRemaining', { left: creditSet.left || 0, allocation: creditSet.allocation || 0 });
};

// Mirror of the interface's `drainOrder`: the set that ends first, then the
// one granted first; a set with no end date goes last.
const drainOrder = (first, second) => {
  const endOf = (creditSet) => (creditSet.expiringAt ? new Date(creditSet.expiringAt).getTime() : Infinity);
  return (endOf(first) - endOf(second)) || (new Date(first.receivedAt) - new Date(second.receivedAt));
};

const isExpired = (creditSet) =>
  Boolean(creditSet.expiringAt) && new Date(creditSet.expiringAt) < new Date();

const formatDate = (value) => getFormattedDate(value, {
  lang: useAuthStore().user?.locale,
  format: 'll',
});

const onOpen = (creditSet) => {
  if (isExpired(creditSet)) {
    return;
  }

  useDialogStore().open('creditSetEdit', {
    creditSet,
    typeKey: typeKeyOf(creditSet),
    workspaceDomain: props.workspaceDomain,
  });
};
</script>
